import {
  ColumnValue,
  Company,
  CpvCode,
  CurrentCompanyUser,
  DocumentTextMatch,
  Lot,
  Query,
  SuperAdminQuery,
} from "@/shared/gql/schema-types";
import { FieldPolicy, Reference } from "@apollo/client";
import {
  KeyFieldsFunction,
  KeySpecifier,
} from "@apollo/client/cache/inmemory/policies";

type MatchesByFilter = Record<string, boolean>;

type PolicyField<Operations extends string> = {
  fields?: Partial<
    Record<
      Operations,
      | FieldPolicy<unknown[]>
      | FieldPolicy<Reference | null>
      | FieldPolicy<MatchesByFilter, boolean, boolean | undefined>
    >
  >;
  keyFields?: KeySpecifier | KeyFieldsFunction | false;
};

// Paginated fields hold one page at a time: a page replaces its predecessor
// rather than being appended to it. Named once because the union in
// `PolicyField` costs an inline function its contextual types.
const replacePage: FieldPolicy<unknown[]>["merge"] = (_existing, incoming) =>
  incoming;

/**
 * A tender the list already loaded is the same tender the detail view asks for,
 * but the cache files it under the list's field (`contents(input:)`) and has
 * never seen `tender(id:)` — so without this the detail view's own read is a
 * miss and the page can only show a skeleton over data it is already holding.
 * Pointing the field at the normalized `Tender` lets any selection the list
 * cached be answered from the cache, and anything it did not (description,
 * agenda, contracts, …) still misses and goes to the network as before.
 *
 * `existing` is `null`, not `undefined`, once the server has answered that a
 * tender does not exist — that answer has to survive, so the redirect only
 * fills in a field the cache has no value for at all. A tender the cache has
 * never seen points at nothing, which reads as missing and fetches exactly as
 * it does today.
 */
const tenderById: FieldPolicy<Reference | null> = {
  read: (existing, { args, toReference }) =>
    existing !== undefined
      ? existing
      : toReference({ __typename: "Tender", id: String(args?.id) }),
};

/**
 * Whether a lot matches is the answer to one list's filter, but the lot is one
 * normalised entity shared by every list (DEV-636). Filed on the lot alone, a
 * filtered list overwrote the unfiltered list's answer, and removing the filter
 * brought back a list that kept hiding the lots the filter had excluded until a
 * page reload. Each answer is filed under the filter that produced it instead;
 * a filter with no answer yet reads as missing, which sends the list to the
 * server.
 */
const filterKey = (variables: Record<string, unknown> | undefined): string => {
  const input = variables?.input as { filter?: unknown } | undefined;

  return JSON.stringify(input?.filter ?? []);
};

const matchesActiveFilterPerFilter: FieldPolicy<
  MatchesByFilter,
  boolean,
  boolean | undefined
> = {
  merge: (existing, incoming, { variables }) => ({
    ...existing,
    [filterKey(variables)]: incoming,
  }),
  read: (existing, { variables }) => existing?.[filterKey(variables)],
};

// Its imporant that a policy in the root is mapped on type not on field name
// That's why i'm using a map here to see a clear relationship between type and field
type FieldPolicyMap = {
  Query: PolicyField<keyof Query>;
  CurrentCompanyUser: PolicyField<keyof CurrentCompanyUser>;
  Company: PolicyField<keyof Company>;
  CpvCode: PolicyField<keyof CpvCode>;
  DocumentTextMatch: PolicyField<keyof DocumentTextMatch>;
  ColumnValue: PolicyField<keyof ColumnValue>;
  Lot: PolicyField<keyof Lot>;
  SuperAdminQuery: PolicyField<keyof SuperAdminQuery>;
};

export const typePolicies: FieldPolicyMap = {
  Query: {},
  CurrentCompanyUser: {
    fields: {
      tender: tenderById,
      contents: {
        // Every input that changes the server's answer has to be in the key, and
        // `viewId`/`viewType` both do: the interactor bypasses filter, search and
        // sort entirely for an Upcoming view and serves TenderStore's own list,
        // and it pushes `viewId` into the request context, which scopes the
        // engagement and review data on each row.
        //
        // Leaving them out collided views that looked identical from the key's
        // point of view. A company's "All", "Upcoming" and any filter-free custom
        // view share one column set, one default sort and `filter: []`, so all
        // three read and wrote a single entry — and since a custom view is
        // cache-first, it rendered whatever the last of them had written without
        // asking the server at all. That is DEV-480: deleting a view's filters
        // returned it to the shared key, where an empty Upcoming page was
        // waiting, and the view showed "No items found" over 99,628 tenders.
        keyArgs: [
          "input",
          [
            "type",
            "viewId",
            "viewType",
            "columns",
            "filter",
            "searchQuery",
            "sortBy",
            "paginator",
            ["page", "take"],
          ],
        ],
        merge: replacePage,
      },
      notifications: {
        keyArgs: ["page", "first", "isRead"],
        merge: replacePage,
      },
    },
  },
  Company: {
    fields: {
      tasks: {
        keyArgs: [
          "input",
          [
            "assignees",
            "tags",
            "searchQuery",
            "completed",
            "paginator",
            ["page", "take"],
          ],
        ],
        merge: replacePage,
      },
    },
  },
  // CPV codes are immutable reference data (~9,454 rows). Normalizing each
  // one inflates the Apollo cache and makes the filter dropdown's first
  // open feel slow.
  CpvCode: { keyFields: false },
  // A match id is only promised to be unique within the document it belongs to, so normalizing
  // it into the shared cache lets one document's passages overwrite another's under the same
  // key — and the next visit to the first document would render the second one's text under its
  // name. It is only ever read as part of its parent search result, so it needs no identity.
  DocumentTextMatch: { keyFields: false },
  // Column lookup values are per-column enum options whose ids (1, 2, 3, ...)
  // are NOT globally unique: procedure, tender type, award criteria, etc. all
  // reuse the same small integers. Normalizing by __typename:id would collide
  // them across columns, so a dropdown could show another column's options
  // (whichever query resolved last). Keep them inline per query result.
  ColumnValue: { keyFields: false },
  // The super-admin namespace has no id, so each query writing `superAdmin` replaced the
  // object the previous one wrote, dropping its fields. As one singleton entity their
  // fields merge, and a sourcing change can evict just the fields it made stale
  // (entities/sourcing-website/api/sourcing-data-changed.ts).
  SuperAdminQuery: { keyFields: [] },
  Lot: {
    fields: {
      matchesActiveFilter: matchesActiveFilterPerFilter,
    },
  },
};
